<template>
  <div class="flexing flex-col w-full">
    <form class="w-full p-6 space-y-6" @submit.prevent>
      <h1
        class="text-texts-standard-important font-medium text-36"
        :class="{ 'relative -bottom-2': isConnect }"
      >
        <pps-account-tooltip :text="$t(message)" :games="games" />
      </h1>
      <p
        class="text-texts-standard-default text-28 font-medium"
        :class="{
          'relative -top-2': isConnect,
        }"
      >
        <template v-if="isSignIn">
          {{ $t('homepage.emailLinkedToPPSAccount') }}
        </template>
        <template v-else-if="isSignUp">
          {{ $t('homepage.PPSNewAccountInfo') }}
        </template>
        <template v-else-if="isConnect">
          {{ $t('homepage.PPSNewAccountInfo') }}
        </template>
      </p>
      <div>
        <div class="v-field--variant-outlined relative">
          <input type="text" name="email" :value="email" disabled />
          <div class="v-field__outline" v-html="inputLabel('homepage.email')" />
          <div
            class="cursor-pointer absolute top-1/2 transform -translate-y-1/2 right-4 text-texts-standard-default text-32 underline uppercase"
            @click="$emit('enter-email')"
          >
            <div class="input-icon is-edit-icon" />
          </div>
        </div>
        <div class="validation-text__wrapper">
          <div class="validation-text" :class="{ 'validation-text--error': errors.email.size > 0 }">
            <span v-for="(error, i) in errors.email" :key="i">
              {{ error }}
            </span>
          </div>
        </div>
      </div>
      <div>
        <div class="v-field--variant-outlined relative">
          <input
            v-model.trim="pwdInput"
            v-focus
            :class="{ 'is-error': errors.password.size > 0 }"
            :type="isPasswordVisible ? 'text' : 'password'"
            name="password"
            :placeholder="$t('homepage.password')"
            :disabled="isLoading"
          />
          <div class="v-field__outline" v-html="inputLabel('homepage.password')" />
          <div
            class="input-icon cursor-pointer absolute top-1/2 transform -translate-y-1/2 right-16"
            :class="isPasswordVisible ? 'is-password-visible-icon' : 'is-password-hidden-icon'"
            @click="isPasswordVisible = !isPasswordVisible"
          />
          <tippy
            class="absolute top-1/2 transform -translate-y-1/2 right-4"
            theme="WSM"
            placement="top"
          >
            <div class="input-icon is-info-icon cursor-pointer" />
            <template #content>
              <p>{{ passwordTooltip }}</p>
            </template>
          </tippy>
        </div>
        <div class="validation-text__wrapper">
          <div
            class="validation-text"
            :class="{ 'validation-text--error': errors.password.size > 0 }"
          >
            <span v-for="(error, i) in errors.password" :key="i">
              {{ error }}
            </span>
          </div>
        </div>
      </div>
      <p v-if="isSignIn" class="text-texts-standard-default text-28">
        <span class="uppercase underline cursor-pointer" @click="$emit('forgotten-pwd')">
          {{ $t('homepage.forgotPassword') }}
        </span>
      </p>
      <app-button
        type="submit"
        :btn-type="isSignUp ? 'secondary' : 'confirm'"
        :disabled="isSubmitBtnDisabled"
        @click="$emit('submit')"
      >
        {{ accountActionButtonLabel }}
        <component-loading class="absolute inset-0" :is-loading="isLoading" />
      </app-button>
      <p v-if="!isConnect" class="text-texts-standard-default text-28">
        <span class="uppercase underline cursor-pointer" @click="$emit('back')">
          {{ $t('homepage.back') }}
        </span>
      </p>
    </form>
  </div>
</template>

<script lang="ts">
import ComponentLoading from '../GlobalComponents/ComponentLoading.vue'
import PpsAccountTooltip from './PpsAccountTooltip.vue'
import { GameId } from '@/globalVariables'
import { PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH } from '@/data/login/constants'
import { defineComponent, type PropType } from 'vue'
import type { Nullable } from '@/interfaces/utils'
import type { FormErrors } from '@/interfaces/login/Login'
import { LoginFlowStep } from '@/enums/LoginFlowStep'

export interface ComponentData {
  PASSWORD_MIN_LENGTH: typeof PASSWORD_MIN_LENGTH
  PASSWORD_MAX_LENGTH: typeof PASSWORD_MAX_LENGTH
  LoginFlowStep: typeof LoginFlowStep
  GameId: typeof GameId
  games: Set<GameId>
  pwdInput: string
  isPasswordVisible: boolean
}

export default defineComponent({
  components: {
    ComponentLoading,
    PpsAccountTooltip,
  },
  props: {
    email: {
      type: String,
      required: true,
    },
    password: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Object as PropType<FormErrors>,
      default: () => {},
    },
    currentStep: {
      type: [Number, null] as PropType<Nullable<LoginFlowStep>>,
      default: null,
    },
    isSignUp: {
      type: Boolean,
      default: false,
    },
    isSignIn: {
      type: Boolean,
      default: false,
    },
    isConnect: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:password', 'back', 'submit', 'enter-email', 'forgotten-pwd'],
  data(): ComponentData {
    return {
      PASSWORD_MIN_LENGTH,
      PASSWORD_MAX_LENGTH,
      LoginFlowStep,
      GameId,
      games: new Set<GameId>(),
      pwdInput: this.password,
      isPasswordVisible: false,
    }
  },
  computed: {
    passwordTooltip(): string {
      return this.$replacePlaceholder(
        this.$replacePlaceholder(
          this.$t('homepage.errorPasswordInvalidFormat'),
          /%s/,
          this.PASSWORD_MIN_LENGTH.toString(),
        ),
        /%s/,
        this.PASSWORD_MAX_LENGTH.toString(),
      )
    },
    isSubmitBtnDisabled(): boolean {
      return (
        this.isLoading ||
        this.email.length === 0 ||
        this.password.length < this.PASSWORD_MIN_LENGTH ||
        this.password.length > this.PASSWORD_MAX_LENGTH ||
        this.errors.email.size > 0 ||
        this.errors.password.size > 0
      )
    },
    accountActionButtonLabel(): string {
      if (this.currentStep === LoginFlowStep.EnterPasswordAndConnect) {
        return this.$t('homepage.continue')
      }
      if (this.currentStep === LoginFlowStep.EnterPasswordAndSignIn) {
        return this.$t('homepage.loginBtn')
      }
      return this.$t('homepage.registerBtn')
    },
    messages(): Record<number, string> {
      return {
        [LoginFlowStep.EnterPasswordAndSignIn]: 'homepage.loginWithPPSAccount',
        [LoginFlowStep.EnterPasswordAndSignUp]: 'homepage.createNewPPSAccount',
        [LoginFlowStep.EnterPasswordAndConnect]: 'settings.linkPPSAccountWithEmail',
      }
    },
    message(): string {
      return this.messages[this.currentStep]
    },
  },
  watch: {
    pwdInput(): void {
      this.$emit('update:password', this.pwdInput)
    },
  },
  methods: {
    inputLabel(translatedLabel: string): string {
      return `
        <div class="v-field__outline__start"></div>
        <div class="v-field__outline__notch">
          <label class="v-label v-field-label" aria-hidden="true">
            ${this.$t(translatedLabel)}
          </label>
        </div>
        <div class="v-field__outline__end"></div>
      `
    },
  },
})
</script>
